import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ZodType } from 'zod';

import { apiClient } from './client';

/**
 * A deep CRUD-resource module behind a small interface: one call produces the verbs
 * (`listFn`/`get`/`create`/`update`/`setStatus`/`remove`), the query-key factory
 * (`keys.all`/`keys.detail`), and the TanStack Query hooks (`useDetail`/`useCreate`/
 * `useUpdate`/`useSetStatus`/`useDelete`) that the product catalog features
 * (devices, components, bundles) previously hand-wrote three identical ways.
 *
 * The genuine per-feature variances are absorbed by config, not by widening the
 * interface:
 *  - **`writeSchema` present → writes parse-and-return the entity** (devices, components);
 *    **absent → writes return `void`** (bundles, whose backend doesn't echo the entity).
 *    `Write` infers from the schema, so the static return type follows the runtime.
 *  - **distinct `CreateBody`/`UpdateBody`** (bundles' PUT body differs from its POST body).
 *  - **`seedDetailOnUpdate`** seeds the detail cache from the update response (components).
 *
 * Meta-queries and uploaders stay hand-written in each feature — their endpoints,
 * schemas, and presign contracts genuinely diverge, so folding them in would widen
 * the interface for no leverage.
 *
 * Curried so the non-inferable phantom types (`CreateBody`, `UpdateBody`, `Status`)
 * are supplied once up front, while everything else infers from the config values:
 *
 *   const devices = createResource<CreateDeviceBody, CreateDeviceBody, DeviceStatus>()({
 *     endpoint: endpoints.products.devices,
 *     keyPrefix: 'devices',
 *     detailSchema: deviceSchema,
 *     listResponseSchema: deviceListResponseSchema,
 *     toQuery: toListQuery,
 *     writeSchema: deviceSchema,
 *   });
 */

type SortOrder = 'asc' | 'desc';

/** Page/sort envelope merged with a feature's filter draft for the list request. */
export type ListParams<Filters> = {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: SortOrder;
} & Filters;

/** Everything the list request sends except paging: sort + filters. The export takes exactly this,
 *  so a file downloads in the order and with the rows the table shows (ADO 120974). */
export type SortedFilters<Filters> = Omit<ListParams<Filters>, 'page' | 'limit'>;

type ResourceConfig<Entity, ListResponse, Filters, Write> = {
  /** Collection endpoint, e.g. `endpoints.products.devices`. Detail ops append `/:id`. */
  endpoint: string;
  /** Query-key root, e.g. `'devices'`. The listing DataTable key shares this prefix, so
   *  invalidating `keys.all` refreshes the list after every write. */
  keyPrefix: string;
  /** Parses GET /:id. Always required — every resource has a detail shape. */
  detailSchema: ZodType<Entity>;
  /** Parses GET (list) `{ data, meta }`. */
  listResponseSchema: ZodType<ListResponse>;
  /** Filter draft → backend query params (the feature's `toListQuery`). */
  toQuery: (filters: Filters) => Record<string, string | number | undefined>;
  /** Default `sortBy` sent when the caller supplies none. Defaults to `'createdAt'`, but not
   *  every resource accepts that column — Components' backend sort enum has `updatedAt`, not
   *  `createdAt`, and 400s on the latter. */
  defaultSortBy?: string;
  /** Present → create/update parse the response and return `Entity`; absent → those writes
   *  return `void`. `setStatus` never uses this — see its own comment. */
  writeSchema?: ZodType<Write>;
  /** Seed the detail cache from the update response before invalidating (components). */
  seedDetailOnUpdate?: boolean;
  /** Other entity roots (`queryKeys.*.all`) a write to this resource changes, invalidated after
   *  every write beside `keys.all` — e.g. a Customer write moves its Dealer's counts. */
  alsoInvalidates?: readonly QueryKey[];
};

/**
 * @typeParam CreateBody - POST body shape.
 * @typeParam UpdateBody - PUT body shape (defaults to `CreateBody`).
 * @typeParam Status - `setStatus` value union (e.g. `'active' | 'inactive'`).
 */
export function createResource<
  CreateBody,
  UpdateBody = CreateBody,
  Status extends string = string,
>() {
  return function build<Entity, ListResponse, Filters, Write = void>(
    config: ResourceConfig<Entity, ListResponse, Filters, Write>,
  ) {
    const { endpoint, keyPrefix, detailSchema, listResponseSchema, toQuery, writeSchema } = config;

    const keys = {
      all: [keyPrefix] as readonly string[],
      detail: (id: string) => [keyPrefix, 'detail', id] as const,
    };

    /** The resource's own root plus every declared dependent. */
    function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
      for (const queryKey of [keys.all, ...(config.alsoInvalidates ?? [])]) {
        void qc.invalidateQueries({ queryKey });
      }
    }

    // void writes (no writeSchema) resolve to undefined; entity writes parse the response.
    // The cast is contained here: when writeSchema is absent, Write is `void`.
    async function parseWrite(res: Promise<unknown>): Promise<Write> {
      const value = await res;
      return (writeSchema ? writeSchema.parse(value) : undefined) as Write;
    }

    /** Sort + filters → wire params, shared by the list request and the export (ADO 120974). */
    function toRequestQuery(params: SortedFilters<Filters>) {
      const { sortBy, sortOrder, ...filters } = params;
      return {
        sortBy: sortBy ?? config.defaultSortBy ?? 'createdAt',
        // Backend's sortOrder enum is uppercase (ASC/DESC); the app carries lowercase
        // internally, so uppercase only at the wire boundary.
        sortOrder: (sortOrder ?? 'desc').toUpperCase(),
        ...toQuery(filters as unknown as Filters),
      };
    }

    async function listFn(params: ListParams<Filters>): Promise<ListResponse> {
      const { page, limit, ...rest } = params;
      const res = await apiClient.getList(endpoint, {
        params: { page, limit, ...toRequestQuery(rest as SortedFilters<Filters>) },
      });
      return listResponseSchema.parse(res);
    }

    async function get(id: string): Promise<Entity> {
      return detailSchema.parse(await apiClient.get(`${endpoint}/${id}`));
    }

    function create(body: CreateBody): Promise<Write> {
      return parseWrite(apiClient.post(endpoint, body));
    }

    function update(id: string, body: UpdateBody): Promise<Write> {
      return parseWrite(apiClient.put(`${endpoint}/${id}`, body));
    }

    // Deliberately does not run the response through `writeSchema` the way create/update do:
    // callers never consume the returned entity (only success/failure — see
    // `use-status-toggle.ts`), and a status-only PATCH's response is not guaranteed to satisfy
    // the full detail schema. Parsing it anyway means a lean response throws a ZodError after
    // the write already succeeded, which the mutation's `catch` then shows as a generic error
    // toast with no cache invalidation (ADO 113116).
    async function setStatus(id: string, status: Status): Promise<void> {
      await apiClient.patch(`${endpoint}/${id}`, { status });
    }

    async function remove(id: string): Promise<void> {
      await apiClient.delete(`${endpoint}/${id}`);
    }

    function useDetail(id: string) {
      return useQuery({
        queryKey: keys.detail(id),
        queryFn: () => get(id),
        enabled: Boolean(id),
      });
    }

    function useCreate() {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (body: CreateBody) => create(body),
        // keyPrefix is the listing DataTable key prefix, so invalidating refreshes the list.
        onSuccess: () => invalidateAll(qc),
      });
    }

    function useUpdate(id: string) {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (body: UpdateBody) => update(id, body),
        onSuccess: (updated) => {
          // Seed the detail cache so re-opening the edit form shows fresh data immediately,
          // then invalidate the tree (listing key is a prefix) to refresh the list.
          if (config.seedDetailOnUpdate && writeSchema) qc.setQueryData(keys.detail(id), updated);
          invalidateAll(qc);
        },
      });
    }

    function useSetStatus() {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: ({ id, status }: { id: string; status: Status }) => setStatus(id, status),
        onSuccess: () => invalidateAll(qc),
      });
    }

    function useDelete() {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (id: string) => remove(id),
        onSuccess: () => invalidateAll(qc),
      });
    }

    return {
      keys,
      listFn,
      toRequestQuery,
      get,
      create,
      update,
      setStatus,
      remove,
      useDetail,
      useCreate,
      useUpdate,
      useSetStatus,
      useDelete,
    };
  };
}
